import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import ButtonLink from '../../components/atoms/button-link/button-link';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import PageContentWithImages from '../../components/molecules/page-content-with-images/page-content-with-images';
import { getFeaturedNewsAndEvents } from '../../services/news';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import {
  getFeaturedFriendsOfTheKitchenPageDetails,
  getFriendsOfTheKitchenPageDetailsBySlug
} from '../../services/inspirations-and-planning';
import {
  friendsOfTheKitchenRoutes,
  inspirationsAndPlanningRoutes,
  pageRoute
} from '../../config/routes/pages';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import * as S from '../../components/atoms/page-section/styles';
import { TabItem } from '../../types/tabs';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEventsContent: NewsAndEventsModel[];
  friendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType;
};

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEventsContent,
    friendsOfTheKitchenPageDetails
  } = props;

  const router = useRouter();

  const breadcrumbTab = friendsOfTheKitchenRoutes.find((route) =>
    isEqual(route.route, router.asPath.split('?')[0])
  );
  const [friendsOfTheKitchen] = inspirationsAndPlanningRoutes;

  return (
    <>
      <TabTitle />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            isReversed
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEventsContent}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbTab as unknown as TabItem}
          tab={friendsOfTheKitchen}
        />
        <div className="mt-0 sm:mt-10 font-ChronicleDisplayRegular text-2header4">
          <h4>{friendsOfTheKitchenPageDetails.pageTitle}</h4>
          <p className="font-MuseoSans100 text-[14px] leading-4 sm:leading-6">
            {friendsOfTheKitchenPageDetails.subtitle}
          </p>
        </div>
        <MarginSpacing margin="mb-3" />
        {router.query['slug'] === 'lea-salonga' ? (
          <div
            style={{
              maxWidth: friendsOfTheKitchenPageDetails.mainImageDimensions.width
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-center md:items-start">
              <div className="hidden md:block">
                <Image
                  alt={friendsOfTheKitchenPageDetails.pageTitle}
                  width={500}
                  height={533.4}
                  src={`${strapiAssetBaseUrl}/left_frame_21039c9f59.png`}
                />
              </div>
              <div>
                <iframe
                  className="aspect-[9/16] w-full h-full"
                  src="https://www.youtube.com/embed/RgF-cXa17OQ?autoplay=1&mute=1"
                  title="Top 5 Kitchen Highlights with Lea Salonga"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
              <div className="hidden md:block">
                <Image
                  alt={friendsOfTheKitchenPageDetails.pageTitle}
                  width={200}
                  height={533.4}
                  src={`${strapiAssetBaseUrl}/right_frame_bb3a2364c1.png`}
                />
              </div>
            </div>
            <S.MarkdownContainer>
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {friendsOfTheKitchenPageDetails.mainImageDescription}
              </ReactMarkdown>
            </S.MarkdownContainer>
          </div>
        ) : null}
        <PageContentWithImages {...friendsOfTheKitchenPageDetails} />
        {router.query['slug'] === 'lea-salonga' ? (
          <div
            style={{
              maxWidth: friendsOfTheKitchenPageDetails.mainImageDimensions.width
            }}
          >
            <div className="flex gap-2">
              <Image
                alt={friendsOfTheKitchenPageDetails.pageTitle}
                width={
                  friendsOfTheKitchenPageDetails.moreDetailsImageDimensions
                    .width
                }
                height={
                  friendsOfTheKitchenPageDetails.moreDetailsImageDimensions
                    .height
                }
                src={friendsOfTheKitchenPageDetails.moreDetailsImage}
              />
              <Image
                alt={friendsOfTheKitchenPageDetails.pageTitle}
                width={
                  friendsOfTheKitchenPageDetails.moreDetailsImageDimensions
                    .width
                }
                height={
                  friendsOfTheKitchenPageDetails.moreDetailsImageDimensions
                    .height
                }
                src={friendsOfTheKitchenPageDetails.moreDetailsImage2}
              />
            </div>
            <S.MarkdownContainer>
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {friendsOfTheKitchenPageDetails.moreDetailsDescription}
              </ReactMarkdown>
            </S.MarkdownContainer>
          </div>
        ) : null}
        <div className="flex justify-end ml-auto mt-10">
          <ButtonLink
            buttonTitle="BACK"
            ctaLink={pageRoute.FRIENDS_OF_KITCHEN}
          />
        </div>
        <MarginSpacing margin="mb-12" />
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps({ params }: any) {
  const featuredNewsAndEventsContent = await getFeaturedNewsAndEvents();
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const friendsOfTheKitchenPageDetails =
    await getFriendsOfTheKitchenPageDetailsBySlug(params.slug);

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEventsContent,
      friendsOfTheKitchenPageDetails
    }
  };
}

export async function getStaticPaths() {
  const paths = friendsOfTheKitchenRoutes.map((route) => {
    return {
      params: { slug: route.slug }
    };
  });

  return {
    paths,
    fallback: false
  };
}

export default Index;
